import { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getRefunds, createRefund, getBookings, getStaff, getBookingRefundInfo } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { SearchableSelect } from '../components/ui/searchable-select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Plus, ArrowCounterClockwise, CreditCard, AmazonLogo, Minus } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function RefundsPage() {
  const { user } = useAuth();
  const [refunds, setRefunds] = useState([]);
  const [completedBookings, setCompletedBookings] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState('all');

  // Form state
  const [selectedBookingId, setSelectedBookingId] = useState('');
  const [refundQty, setRefundQty] = useState(0);
  const [refundReason, setRefundReason] = useState('');
  const [refundInfo, setRefundInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(false);

  // Confirmation dialog
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isOwner = user?.role === 'admin' || user?.role === 'owner';
  const isAdminOrAbove = isOwner || user?.role === 'assistant';
  const isAdmin = isAdminOrAbove;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const staffId = selectedStaff === 'all' ? undefined : selectedStaff;
      const [refundsRes, bookingsRes] = await Promise.all([
        getRefunds(staffId),
        getBookings(staffId)
      ]);
      setRefunds(refundsRes.data);
      setCompletedBookings(
        bookingsRes.data.filter(b =>
          (b.status === 'completed' || b.status === 'partially_refunded') && !b.is_reversed
        )
      );
      if (isAdmin) {
        const staffRes = await getStaff();
        setStaffList(staffRes.data);
      }
    } catch (err) {
      toast.error('Data fetch nahi ho paya');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, selectedStaff]); // API functions are stable imports

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!isAdminOrAbove) {
    return <Navigate to="/" replace />;
  }

  const formatCurrency = (n) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n || 0);

  const handleBookingSelect = async (bookingId) => {
    setSelectedBookingId(bookingId);
    setRefundQty(0);
    setRefundInfo(null);
    if (!bookingId) return;

    setLoadingInfo(true);
    try {
      const res = await getBookingRefundInfo(bookingId);
      setRefundInfo(res.data);
    } catch (err) {
      toast.error('Booking info nahi mil paya');
    } finally {
      setLoadingInfo(false);
    }
  };

  const remainingQty = refundInfo?.remaining_qty || 0;
  const bookingRate = refundInfo?.booking_rate || 0;
  const calculatedAmount = Math.round(bookingRate * refundQty * 100) / 100;

  const handleQtyChange = (newQty) => {
    const clamped = Math.max(0, Math.min(newQty, remainingQty));
    setRefundQty(clamped);
  };

  const handleSubmitRefund = () => {
    if (!selectedBookingId) { toast.error('Booking select karo'); return; }
    if (refundQty <= 0) { toast.error('Kam se kam 1 cylinder select karo'); return; }
    if (refundQty > remainingQty) {
      toast.error(`Max ${remainingQty} cylinder refund ho sakta hai`);
      return;
    }
    setConfirmOpen(true);
  };

  const processRefund = async () => {
    setConfirmOpen(false);
    try {
      await createRefund({
        booking_id: selectedBookingId,
        quantity: refundQty,
        reason: refundReason || undefined
      });
      toast.success(`${refundQty} cylinder refund ho gaya! (${formatCurrency(calculatedAmount)})`);
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Refund fail ho gaya');
    }
  };

  const resetForm = () => {
    setSelectedBookingId('');
    setRefundQty(0);
    setRefundReason('');
    setRefundInfo(null);
  };

  const getRefundBadge = (status) => {
    if (status === 'completed') return <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Completed</span>;
    return <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">{status}</span>;
  };

  const getRefundToBadge = (to) => {
    if (to === 'amazon_account') return (
      <span className="inline-flex items-center gap-1 text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
        <AmazonLogo size={12} weight="fill" /> Amazon
      </span>
    );
    return (
      <span className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
        <CreditCard size={12} weight="fill" /> Card
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="refunds-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <ArrowCounterClockwise size={28} weight="fill" className="text-[#002FA7]" /> Refunds
        </h1>
        <div className="flex gap-3 items-center">
          {isAdmin && (
            <SearchableSelect
              options={[
                { value: 'all', label: 'All Staff' },
                ...staffList.map(s => ({ value: s.id, label: s.name, sublabel: s.email }))
              ]}
              value={selectedStaff}
              onValueChange={setSelectedStaff}
              placeholder="Filter Staff"
              className="w-48"
              data-testid="refund-staff-filter"
            />
          )}
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <Button onClick={() => setDialogOpen(true)} className="btn-primary" data-testid="new-refund-btn">
              <Plus size={18} className="mr-2" /> Process Refund
            </Button>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-lg">
                  <ArrowCounterClockwise size={22} className="text-[#002FA7]" /> Cylinder Refund
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {/* Booking Select */}
                <div>
                  <Label className="text-sm font-medium text-gray-700">Booking *</Label>
                  <SearchableSelect
                    options={completedBookings.map(b => ({
                      value: b.id,
                      label: b.agency_name
                    }))}
                    value={selectedBookingId}
                    onValueChange={handleBookingSelect}
                    placeholder="Booking select karo"
                    searchPlaceholder="Search..."
                    data-testid="refund-booking-select"
                  />
                </div>

                {/* Clean Booking Info */}
                {loadingInfo && <p className="text-sm text-gray-400">Loading...</p>}
                {refundInfo && (
                  <div className="bg-gray-50 rounded-lg p-3 space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Agency</span>
                      <span className="font-semibold text-gray-900">{refundInfo.agency_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Rate</span>
                      <span className="font-semibold">{formatCurrency(refundInfo.booking_rate)}</span>
                    </div>
                    <div className="border-t border-gray-200 my-1" />
                    <div className="flex justify-between" data-testid="refund-booked-qty">
                      <span className="text-gray-500">Booked</span>
                      <span className="font-bold text-blue-700">{refundInfo.booking_quantity} cylinder</span>
                    </div>
                    <div className="flex justify-between" data-testid="refund-already-refunded-qty">
                      <span className="text-gray-500">Refunded</span>
                      <span className="font-semibold text-orange-600">{refundInfo.total_refunded_qty} cylinder</span>
                    </div>
                    <div className="border-t border-dashed border-gray-300 my-1" />
                    <div className="flex justify-between" data-testid="refund-remaining-qty">
                      <span className="text-gray-700 font-medium">Remaining</span>
                      <span className="font-bold text-green-700">{refundInfo.remaining_qty} cylinder</span>
                    </div>
                    {refundInfo.source === 'amazon' && (
                      <div className="text-xs text-orange-600 flex items-center gap-1 pt-1">
                        <AmazonLogo size={12} weight="fill" /> Amazon Account mein jayega
                      </div>
                    )}
                  </div>
                )}

                {/* Quantity Input + Quick Buttons */}
                {refundInfo && remainingQty > 0 && (
                  <>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Kitna refund? *</Label>
                      <div className="flex items-center gap-3 mt-2">
                        <button
                          onClick={() => handleQtyChange(refundQty - 1)}
                          disabled={refundQty <= 0}
                          className="w-10 h-10 rounded-lg border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          data-testid="refund-qty-minus"
                        >
                          <Minus size={18} weight="bold" />
                        </button>
                        <div className="flex-1 text-center bg-white border-2 border-[#002FA7] rounded-lg py-2.5" data-testid="refund-qty-display">
                          <span className="text-3xl font-bold text-[#002FA7]">{refundQty}</span>
                          <span className="text-sm text-gray-400 ml-1">cylinder</span>
                        </div>
                        <button
                          onClick={() => handleQtyChange(refundQty + 1)}
                          disabled={refundQty >= remainingQty}
                          className="w-10 h-10 rounded-lg border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          data-testid="refund-qty-plus"
                        >
                          <Plus size={18} weight="bold" />
                        </button>
                      </div>
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={() => handleQtyChange(1)}
                          className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all ${refundQty === 1 ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-gray-600 border-gray-300 hover:border-[#002FA7]'}`}
                          data-testid="refund-quick-1"
                        >
                          +1
                        </button>
                        <button
                          onClick={() => handleQtyChange(remainingQty)}
                          className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all ${refundQty === remainingQty ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-gray-600 border-gray-300 hover:border-[#002FA7]'}`}
                          data-testid="refund-quick-full"
                        >
                          Full
                        </button>
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs text-gray-500">Reason (optional)</Label>
                      <Textarea
                        value={refundReason}
                        onChange={(e) => setRefundReason(e.target.value)}
                        placeholder="Reason..."
                        className="min-h-[48px] text-sm mt-1"
                        data-testid="refund-reason-input"
                      />
                    </div>

                    <Button
                      onClick={handleSubmitRefund}
                      disabled={refundQty <= 0}
                      className="w-full btn-primary"
                      data-testid="refund-submit-btn"
                    >
                      Submit Refund
                    </Button>
                  </>
                )}

                {refundInfo && remainingQty === 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center" data-testid="refund-all-done">
                    <p className="text-red-700 font-medium text-sm">Sab cylinder refund ho chuke hain!</p>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Refunds Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              {isAdmin && <TableHead>Staff</TableHead>}
              <TableHead>Agency</TableHead>
              <TableHead className="text-center">Qty</TableHead>
              <TableHead className="text-right">Rate</TableHead>
              <TableHead className="text-right">Refund Amt</TableHead>
              <TableHead>Refund To</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Approved By</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {refunds.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isAdmin ? 10 : 9} className="text-center text-gray-500">
                  No refunds found
                </TableCell>
              </TableRow>
            ) : (
              refunds.map((r) => (
                <TableRow key={r.id} data-testid={`refund-row-${r.id}`}>
                  {isAdmin && <TableCell className="font-medium">{r.staff_name}</TableCell>}
                  <TableCell>{r.agency_name || '-'}</TableCell>
                  <TableCell className="text-center">
                    <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 font-bold px-2 py-0.5 rounded text-sm" data-testid={`refund-qty-${r.id}`}>
                      {r.quantity || 0} cyl
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm text-gray-600">
                    {formatCurrency(r.booking_rate || 0)}
                  </TableCell>
                  <TableCell className="text-right font-mono font-semibold text-green-600">
                    {formatCurrency(r.amount)}
                    {r.booking_quantity && r.quantity < r.booking_quantity && (
                      <span className="block text-xs text-orange-500">Partial ({r.quantity}/{r.booking_quantity})</span>
                    )}
                  </TableCell>
                  <TableCell>{getRefundToBadge(r.refund_to)}</TableCell>
                  <TableCell>{getRefundBadge(r.status)}</TableCell>
                  <TableCell>
                    {r.approved_by ? (
                      <div className="text-sm">
                        <span className="font-medium">{r.approved_by}</span>
                        <span className="block text-xs text-gray-500">
                          {r.approved_by_role === 'admin' ? 'Owner' : r.approved_by_role === 'assistant' ? 'Assistant' : r.approved_by_role}
                        </span>
                      </div>
                    ) : '-'}
                  </TableCell>
                  <TableCell className="text-gray-500 max-w-[120px] truncate">{r.reason || '-'}</TableCell>
                  <TableCell className="text-sm">{formatDateTime(r.created_at)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#002FA7]">Confirm Refund?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 pt-2">
                <p className="text-lg font-bold text-gray-900">
                  {refundQty} cylinder — {refundInfo?.agency_name}
                </p>
                <p className="text-xs text-gray-500">Undo nahi hoga.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={processRefund} className="bg-[#002FA7] hover:bg-[#001f70]" data-testid="refund-confirm-btn">
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
